/**
 * Currículo do usuário (RN-014): cartões por seção — formação, experiência, cursos e
 * certificações, registros profissionais — com incluir, editar e excluir. Design system:
 * cabeçalho de seção com ícone, cartão com barra lateral na cor da seção, ações redondas.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Badge, Delete, Edit, School, Verified, WorkOutline } from "@mui/icons-material";
import ConfirmModal from "./modals/ConfirmModal";
import CurriculumEntryModal, { CURRICULUM_KINDS } from "./modals/CurriculumEntryModal";
import { colors } from "../theme/designSystem";
import {
  usersService,
  type CurriculumEntry,
  type CurriculumEntryPayload,
  type CurriculumKind,
} from "../services/usersService";

const KIND_ICON: Record<CurriculumKind, React.ReactElement> = {
  formacao: <School sx={{ fontSize: "1.1rem" }} />,
  experiencia: <WorkOutline sx={{ fontSize: "1.1rem" }} />,
  certificacao: <Verified sx={{ fontSize: "1.1rem" }} />,
  registro: <Badge sx={{ fontSize: "1.1rem" }} />,
};

const KIND_COLOR: Record<CurriculumKind, string> = {
  formacao: colors.primary,
  experiencia: "#6f42c1",
  certificacao: colors.success,
  registro: "#fd7e14",
};

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "2019-03-01" -> "mar/2019". Só data: não passa por Date para não deslocar fuso. */
const mesAno = (date: string | null): string => {
  if (!date) return "";
  const [y, m] = date.split("-");
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${MESES[idx]}/${y}` : y;
};

const periodo = (e: CurriculumEntry): string => {
  const inicio = mesAno(e.startedAt);
  const fim = e.current ? "atual" : mesAno(e.endedAt);
  if (!inicio && !fim) return "";
  if (!inicio) return fim;
  if (!fim) return inicio;
  return `${inicio} – ${fim}`;
};

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const CurriculumSection: React.FC<Props> = ({ onError, onSuccess }) => {
  const [entries, setEntries] = useState<CurriculumEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CurriculumEntry | null>(null);
  const [defaultKind, setDefaultKind] = useState<CurriculumKind>("formacao");
  const [toDelete, setToDelete] = useState<CurriculumEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await usersService.getMyCurriculum());
    } catch {
      onError("Não foi possível carregar o currículo.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const porTipo = useMemo(
    () => CURRICULUM_KINDS.map((k) => ({ ...k, itens: entries.filter((e) => e.kind === k.value) })),
    [entries],
  );

  const abrirNovo = (kind: CurriculumKind) => {
    setEditing(null);
    setDefaultKind(kind);
    setModalOpen(true);
  };

  const abrirEdicao = (e: CurriculumEntry) => {
    setEditing(e);
    setModalOpen(true);
  };

  const salvar = async (payload: CurriculumEntryPayload) => {
    setSaving(true);
    try {
      if (editing) {
        await usersService.updateCurriculumEntry(editing.id, payload);
      } else {
        await usersService.createCurriculumEntry(payload);
      }
      setModalOpen(false);
      onSuccess("Currículo atualizado.");
      await load();
    } catch (e) {
      onError((e as { response?: { data?: { description?: string } } })?.response?.data?.description ?? "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const excluir = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await usersService.deleteCurriculumEntry(toDelete.id);
      setToDelete(null);
      onSuccess("Item removido do currículo.");
      await load();
    } catch {
      onError("Não foi possível excluir.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: colors.textPrimary, mb: 1.5 }}>Currículo</Typography>

      {!loading && entries.length === 0 && (
        <Box className="c4u-info-box" sx={{ mb: 2 }}>
          <Typography>
            Seu currículo aparece para a equipe e, no futuro, nos relatórios e documentos que levam o
            nome do profissional. Comece pela formação.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {porTipo.map((secao) => (
          <Box key={secao.value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{ color: KIND_COLOR[secao.value], display: "flex" }}>{KIND_ICON[secao.value]}</Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textSecondary, textTransform: "uppercase", letterSpacing: 0.4, flex: 1 }}>
                {secao.label}
              </Typography>
              <Tooltip title={`Incluir ${secao.label.toLowerCase()}`}>
                <IconButton
                  size="small"
                  onClick={() => abrirNovo(secao.value)}
                  sx={{ width: 28, height: 28, color: KIND_COLOR[secao.value], border: `1px solid ${KIND_COLOR[secao.value]}` }}
                >
                  <Typography sx={{ fontSize: 18, lineHeight: 1, fontWeight: 600 }}>+</Typography>
                </IconButton>
              </Tooltip>
            </Box>

            {secao.itens.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: "#adb5bd", pl: 3.5 }}>Nada cadastrado.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {secao.itens.map((e) => (
                  <Box
                    key={e.id}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      p: 1.5,
                      borderRadius: "8px",
                      border: `1px solid ${colors.border}`,
                      borderLeft: `3px solid ${KIND_COLOR[e.kind]}`,
                      backgroundColor: colors.white,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>{e.title}</Typography>
                      {(e.institution || periodo(e)) && (
                        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                          {[e.institution, periodo(e)].filter(Boolean).join(" · ")}
                          {e.current && (
                            <Box component="span" sx={{ ml: 1, px: 0.8, py: 0.1, borderRadius: "10px", fontSize: 11, fontWeight: 600, color: colors.success, backgroundColor: "#e8f6ec" }}>
                              atual
                            </Box>
                          )}
                        </Typography>
                      )}
                      {e.description && (
                        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.5, whiteSpace: "pre-line" }}>
                          {e.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Editar">
                        <IconButton className="c4u-row-action" size="small" onClick={() => abrirEdicao(e)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton className="c4u-row-action" size="small" onClick={() => setToDelete(e)} sx={rowActionSx("#dc3545", "#c82333")}>
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <CurriculumEntryModal
        open={modalOpen}
        entry={editing}
        defaultKind={defaultKind}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSave={salvar}
      />

      <ConfirmModal
        isOpen={toDelete !== null}
        title="Excluir item do currículo"
        message={toDelete ? `Excluir "${toDelete.title}"? Esta ação não pode ser desfeita.` : ""}
        confirmButtonText="Excluir"
        type="danger"
        isProcessing={saving}
        onConfirm={excluir}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
};

export default CurriculumSection;
